import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Users, MapPin, Clock, CheckCircle, Loader2, ArrowRight,
  Home, Flame, Bookmark, BookmarkCheck, ExternalLink, UserCircle,
  DoorOpen, UserPlus, LogIn, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/components/AuthContext";
import { CollectivesGridSkeleton } from "@/components/Skeletons";

import API from "@/apiConfig";
// Auth URL must use window.location.origin so it works on ANY domain
// (production, preview, custom domain) — never REACT_APP_BACKEND_URL
const getGoogleAuthUrl = () =>
  `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth-callback")}`;
const WAGC_SAVED_KEY = "wagc_my_collectives";
const AREA_FILTERS = ["All", "Carthage", "Gladewater", "Shreveport", "Online"];

const getSavedIds = () => {
  try { return JSON.parse(localStorage.getItem(WAGC_SAVED_KEY) || "[]"); }
  catch { return []; }
};

// Placeholder avatar using area initial when no photo_url
function CollectiveAvatar({ collective }) {
  if (collective.photo_url) {
    return (
      <img
        src={collective.photo_url}
        alt={collective.display_name || `The Collective @ ${collective.area || 'Unknown'}`}
        className="w-full h-full object-cover"
      />
    );
  }
  const colors = {
    Carthage: "bg-navy",
    Gladewater: "bg-emerald-900",
    Shreveport: "bg-purple-900",
    Online: "bg-gray-700",
  };
  const bg = colors[collective.area] || "bg-navy";
  return (
    <div className={`w-full h-full ${bg} flex items-center justify-center`}>
      <Home className="h-8 w-8 text-gold/60" />
    </div>
  );
}

export default function CollectivesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [collectives, setCollectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeArea, setActiveArea] = useState("All");
  const [selectedCollective, setSelectedCollective] = useState(null);
  const [joinForm, setJoinForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinDone, setJoinDone] = useState(false);
  const [savedIds, setSavedIds] = useState(getSavedIds);
  const [membershipMap, setMembershipMap] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  useEffect(() => {
    // Instant paint from cache (stale-while-revalidate) so cards appear immediately.
    try {
      const cached = JSON.parse(localStorage.getItem("wagc_collectives_cache") || "null");
      if (Array.isArray(cached) && cached.length) {
        setCollectives(cached);
        setLoading(false);
      }
    } catch { /* ignore bad cache */ }
    axios
      .get(`${API}/collectives`, { timeout: 20000 })
      .then((r) => {
        const list = r.data.filter((c) => !c.is_restricted);
        setCollectives(list);
        try { localStorage.setItem("wagc_collectives_cache", JSON.stringify(list)); } catch { /* quota */ }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Check membership for all collectives when user is authenticated
  const checkMemberships = useCallback(async () => {
    if (!user || collectives.length === 0) {
      setMembershipMap({});
      return;
    }
    const map = {};
    await Promise.all(
      collectives.map(async (c) => {
        try {
          const token = localStorage.getItem("auth_token") || localStorage.getItem("wagc_session_token") || "";
          const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : { withCredentials: true };
          const r = await axios.get(`${API}/collectives/${c.id}/access`, { ...cfg, timeout: 8000 });
          map[c.id] = r.data.is_member || false;
        } catch {
          map[c.id] = false;
        }
      })
    );
    setMembershipMap(map);
  }, [user, collectives]);

  useEffect(() => {
    checkMemberships();
  }, [checkMemberships]);

  // Post-login continuation: check for pending collective
  useEffect(() => {
    if (!user || collectives.length === 0 || Object.keys(membershipMap).length === 0) return;
    const pendingSlug = localStorage.getItem("wagc_pending_collective");
    if (!pendingSlug) return;
    localStorage.removeItem("wagc_pending_collective");
    const collective = collectives.find((c) => c.slug === pendingSlug);
    if (!collective) return;
    if (membershipMap[collective.id]) {
      navigate(`/collectives/${collective.slug}/room`);
    } else {
      handleOpenJoin(collective);
    }
  }, [user, collectives, membershipMap]); // eslint-disable-line

  const filtered = activeArea === "All"
    ? collectives
    : collectives.filter((c) => c.area === activeArea);

  const handleJoin = async () => {
    if (!joinForm.name || !joinForm.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setJoinLoading(true);
    try {
      await axios.post(`${API}/collectives/${selectedCollective.id}/join`, joinForm);
      const next = savedIds.includes(selectedCollective.id)
        ? savedIds
        : [...savedIds, selectedCollective.id];
      setSavedIds(next);
      localStorage.setItem(WAGC_SAVED_KEY, JSON.stringify(next));
      setJoinDone(true);
      toast.success("Request submitted! Your Guide Pair will reach out to you.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setJoinLoading(false);
    }
  };

  const toggleSave = (e, id) => {
    e.stopPropagation();
    const next = savedIds.includes(id)
      ? savedIds.filter((x) => x !== id)
      : [...savedIds, id];
    setSavedIds(next);
    localStorage.setItem(WAGC_SAVED_KEY, JSON.stringify(next));
    toast.success(next.includes(id) ? "Saved to My Collectives" : "Removed from My Collectives");
  };

  const handleOpenJoin = (collective) => {
    setSelectedCollective(collective);
    setJoinForm({ name: user?.name || "", email: user?.email || "", phone: "", message: "" });
    setJoinDone(false);
  };

  const getGuidePairLabel = (collective) => {
    const gp = collective.guide_pair_names;
    if (gp?.leader1_name) {
      return gp.leader2_name
        ? `${gp.leader1_name} & ${gp.leader2_name}`
        : gp.leader1_name;
    }
    return null;
  };

  const handleCardClick = (collective) => {
    // Direct token check — independent of AuthContext's async /me call.
    // If a token exists, the user IS authenticated regardless of whether
    // the AuthContext user object has been populated yet.
    const hasToken = Boolean(
      localStorage.getItem("auth_token") || localStorage.getItem("wagc_session_token")
    );

    if (hasToken || user) {
      // Authenticated — navigate directly, never trigger auth redirect
      if (membershipMap[collective.id]) {
        navigate(`/collectives/${collective.slug}/room`);
      } else {
        handleOpenJoin(collective);
      }
      return;
    }

    if (authLoading) {
      toast.info("Checking your sign-in status — try again in a moment.");
      return;
    }

    // Truly unauthenticated (no token, no user) → store intent, redirect to sign-in
    localStorage.setItem("wagc_pending_collective", collective.slug);
    localStorage.setItem("wagc_auth_redirect", "/collectives");
    const names = getGuidePairLabel(collective);
    toast.info(
      names
        ? `Sign in to join ${collective.display_name || `The Collective @ ${collective.area}`} with ${names}`
        : `Sign in to join ${collective.display_name || `The Collective @ ${collective.area}`}`,
      {
        description: "We'll take you right back after you sign in.",
        duration: 6000,
      }
    );
    window.location.href = getGoogleAuthUrl();
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-navy py-16 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold rounded-full blur-3xl" />
        </div>
        <div className="container-page relative z-10 text-center">
          <Home className="h-8 w-8 text-gold mx-auto mb-3" />
          <div className="gold-line-center mb-4" />
          <h1 className="font-heading text-4xl sm:text-5xl mb-4">The Collectives</h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            "Day by day, continuing with one mind in the temple, and breaking bread from house to house..."
          </p>
          <p className="text-gold font-semibold mt-2 text-sm tracking-wide">— Acts 2:46</p>
        </div>
      </section>

      {/* What is a Collective */}
      <section className="py-16 bg-bone">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="gold-line-center mb-4" />
          <h2 className="font-heading text-2xl text-navy mb-4">Church in the Home</h2>
          <p className="text-gray-600 leading-relaxed text-base">
            The Collectives are <strong className="text-navy">home-based gatherings</strong> — not services, not programs.
            They are the primary expression of church at Word Alive Global Center: people coming together in homes for
            worship, teaching, prayer, and genuine fellowship. This is where the early church gathered,
            and it's where we gather today.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            {["Worship", "Teaching", "Prayer", "Fellowship", "Community"].map((tag) => (
              <Badge key={tag} className="bg-navy/10 text-navy border border-navy/20 rounded-sm px-3 py-1">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Collectives Directory */}
      <section className="py-16 bg-bone">
        <div className="container-page">
          {/* Header + filter chips */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="gold-line mb-3" />
              <h2 className="font-heading text-3xl text-navy">Find a Collective Near You</h2>
              <p className="text-gray-500 text-sm mt-1">
                {collectives.length} active Collective{collectives.length !== 1 ? "s" : ""} across the region
              </p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-gray-400 text-xs">Subscribe to all events:</span>
                <a
                  href="https://calendar.google.com/calendar/render?cid=c_aigm6hltac2scnskjvnv1h6m0g%40group.calendar.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="subscribe-google-calendar"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gold hover:text-gold/80 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CalendarPlus className="h-3.5 w-3.5" />
                  Google Calendar
                </a>
                <a
                  href="webcal://calendar.google.com/calendar/ical/c_aigm6hltac2scnskjvnv1h6m0g%40group.calendar.google.com/public/basic.ics"
                  data-testid="subscribe-apple-calendar"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gold hover:text-gold/80 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <CalendarPlus className="h-3.5 w-3.5" />
                  Apple &amp; Other
                </a>
              </div>
            </div>
          </div>

          {/* Area filter chips */}
          <div
            className="flex gap-2 overflow-x-auto pb-2 mb-10 scrollbar-hide"
            role="group"
            aria-label="Filter by area"
            data-testid="area-filters"
          >
            {AREA_FILTERS.map((area) => {
              const count = area === "All"
                ? collectives.length
                : collectives.filter((c) => c.area === area).length;
              return (
                <button
                  key={area}
                  data-testid={`filter-${area.toLowerCase()}`}
                  onClick={() => setActiveArea(area)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                    activeArea === area
                      ? "bg-navy text-white border-navy"
                      : "bg-white text-gray-600 border-bone-dark hover:border-gold/40 hover:text-navy"
                  }`}
                >
                  {area}
                  {count > 0 && (
                    <span className={`text-xs rounded-full px-1.5 font-bold ${
                      activeArea === area ? "bg-gold text-navy" : "bg-gray-100 text-gray-500"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {loading ? (
            <CollectivesGridSkeleton count={6} />
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-bone rounded-sm border border-bone-dark">
              <Home className="h-10 w-10 text-gold/30 mx-auto mb-3" />
              <p className="font-heading text-xl text-navy mb-2">No Collectives in This Area Yet</p>
              <p className="text-gray-500 text-sm mb-4">Be the first to start one.</p>
              <Button
                className="btn-gold"
                onClick={() => (window.location.href = "mailto:contactus@wordalivetx.com?subject=Starting a Collective")}
              >
                Inquire About Starting One
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((collective) => {
                const isMember = membershipMap[collective.id];
                const isLoggedIn = !!user;
                return (
                <Card
                  key={collective.id}
                  className="bg-white shadow-sm overflow-hidden border border-bone-dark cursor-pointer transition-all duration-200 hover:border-gold/50 hover:shadow-md focus-within:border-gold/50 focus-within:shadow-md"
                  data-testid={`collective-card-${collective.id}`}
                  onClick={() => handleCardClick(collective)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCardClick(collective); }}
                >
                  {/* Photo / Avatar */}
                  <div className="relative h-40 overflow-hidden bg-navy/5">
                    <CollectiveAvatar collective={collective} />
                    {/* Top-right controls */}
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                      <button
                        data-testid={`save-collective-${collective.id}`}
                        onClick={(e) => toggleSave(e, collective.id)}
                        className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-500 hover:text-gold transition-colors shadow-sm"
                        title={savedIds.includes(collective.id) ? "Remove from My Collectives" : "Save to My Collectives"}
                      >
                        {savedIds.includes(collective.id)
                          ? <BookmarkCheck className="h-4 w-4 text-gold" />
                          : <Bookmark className="h-4 w-4" />
                        }
                      </button>
                      {collective.is_accepting ? (
                        <Badge className="bg-green-600 text-white border-0 rounded-full text-xs font-semibold">
                          Open
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500 text-white border-0 rounded-full text-xs">
                          Full
                        </Badge>
                      )}
                    </div>
                    {/* Bottom-left state badge */}
                    <div className="absolute bottom-3 left-3">
                      {isMember ? (
                        <Badge className="bg-gold text-navy border-0 rounded-sm text-xs font-semibold shadow-sm px-2.5 py-1" data-testid={`badge-enter-${collective.id}`}>
                          <DoorOpen className="h-3 w-3 mr-1" /> Enter Room
                        </Badge>
                      ) : isLoggedIn ? (
                        <Badge className="bg-white/90 backdrop-blur-sm text-navy border border-navy/20 rounded-sm text-xs font-medium shadow-sm px-2.5 py-1" data-testid={`badge-join-${collective.id}`}>
                          <UserPlus className="h-3 w-3 mr-1" /> Request to Join
                        </Badge>
                      ) : (
                        <Badge className="bg-white/90 backdrop-blur-sm text-navy border border-navy/20 rounded-sm text-xs font-medium shadow-sm px-2.5 py-1" data-testid={`badge-signin-${collective.id}`}>
                          <LogIn className="h-3 w-3 mr-1" /> Sign in to Join
                        </Badge>
                      )}
                    </div>
                  </div>

                  <CardContent className="p-6">
                    {/* Name */}
                    <h3 className="font-heading text-xl text-navy mb-2">{collective.display_name || `The Collective @ ${collective.area || 'Unknown'}`}</h3>

                    {/* Guide Pair — unified from guide_pair_names */}
                    <div className="flex items-center gap-1.5 text-gray-600 text-sm mb-2">
                      <UserCircle className="h-3.5 w-3.5 text-gold shrink-0" />
                      {collective.guide_pair_names?.leader1_name ? (
                        <span>
                          Led by{" "}
                          <strong className="text-navy">{collective.guide_pair_names.leader1_name}</strong>
                          {collective.guide_pair_names.leader2_name && (
                            <> &amp; <strong className="text-navy">{collective.guide_pair_names.leader2_name}</strong></>
                          )}
                        </span>
                      ) : (
                        <span className="italic text-gray-400">Led by Collective Leadership</span>
                      )}
                    </div>

                    {/* Location — tappable Maps link */}
                    <a
                      href={collective.maps_url || `https://maps.google.com/?q=${encodeURIComponent(collective.location_text || collective.area)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`maps-link-${collective.id}`}
                      className="flex items-center gap-1.5 text-sm mb-2 text-gold hover:text-gold/80 transition-colors group"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="underline underline-offset-2 group-hover:no-underline">
                        {collective.location_text || collective.area}
                      </span>
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>

                    {/* Schedule */}
                    <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-4">
                      <Clock className="h-3.5 w-3.5 text-gold shrink-0" />
                      {collective.meeting_schedule}
                    </div>

                    {/* Description */}
                    {collective.description && (
                      <div className="mb-4">
                        <p 
                          className={`text-gray-600 text-sm leading-relaxed ${
                            expandedDescriptions[collective.id] ? '' : 'line-clamp-3'
                          }`}
                          data-testid={`description-${collective.id}`}
                        >
                          {collective.description}
                        </p>
                        {collective.description.length > 150 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedDescriptions(prev => ({
                                ...prev,
                                [collective.id]: !prev[collective.id]
                              }));
                            }}
                            className="text-gold text-xs font-medium hover:text-gold/80 mt-1 transition-colors"
                            data-testid={`read-more-${collective.id}`}
                          >
                            {expandedDescriptions[collective.id] ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-bone-dark">
                      <span className="text-xs text-gray-400">
                        {collective.guide_pair_names?.leader1_name
                          ? `Led by ${collective.guide_pair_names.leader1_name}${collective.guide_pair_names.leader2_name ? ` & ${collective.guide_pair_names.leader2_name}` : ""}`
                          : <span className="italic">Led by Collective Leadership</span>
                        }
                      </span>
                      <span className="text-xs text-gold font-medium flex items-center gap-1">
                        {isMember ? (
                          <><DoorOpen className="h-3 w-3" /> Enter</>
                        ) : (
                          <><ArrowRight className="h-3 w-3" /> {isLoggedIn ? "Join" : "Sign in"}</>
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Start a Collective CTA */}
      <section className="py-20 bg-navy text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Flame className="h-8 w-8 text-gold mx-auto mb-5" />
          <div className="gold-line-center mb-4" />
          <h2 className="font-heading text-3xl mb-4">Not Near Any Collective?</h2>
          <p className="text-white/70 text-base leading-relaxed mb-8">
            You could be the one to start a new gathering in your neighborhood. It begins
            with just a few people, a home, and a heart for God. Reach out and we'll walk you through it.
          </p>
          <Button
            data-testid="start-collective-btn"
            className="btn-gold px-8 py-3 uppercase tracking-wider font-medium"
            onClick={() => (window.location.href = "mailto:contactus@wordalivetx.com?subject=Starting a Collective")}
          >
            Inquire About Starting One
          </Button>
        </div>
      </section>

      {/* Join Request Dialog */}
      <Dialog
        open={!!selectedCollective}
        onOpenChange={() => { setSelectedCollective(null); setJoinDone(false); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-navy">
              Join {selectedCollective?.display_name || `The Collective @ ${selectedCollective?.area || 'Unknown'}`}
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-sm">
              <span className="flex items-center gap-2 mt-1 flex-wrap">
                <a
                  href={selectedCollective?.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gold hover:underline"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {selectedCollective?.location_text || selectedCollective?.area}
                </a>
                <span className="text-gray-300">·</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {selectedCollective?.meeting_schedule}
                </span>
              </span>
            </DialogDescription>
          </DialogHeader>

          {joinDone ? (
            <div className="text-center py-6" data-testid="join-success">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <p className="font-heading text-xl text-navy mb-2">Request Received!</p>
              <p className="text-gray-500 text-sm">
                Your Guide Pair will reach out to you soon. Welcome to the family!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-navy text-sm">Your Name *</Label>
                <Input
                  data-testid="join-name"
                  value={joinForm.name}
                  onChange={(e) => setJoinForm((f) => ({ ...f, name: e.target.value }))}
                  className="border-bone-dark focus:border-gold rounded-sm mt-1"
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label className="text-navy text-sm">Email *</Label>
                <Input
                  data-testid="join-email"
                  type="email"
                  value={joinForm.email}
                  onChange={(e) => setJoinForm((f) => ({ ...f, email: e.target.value }))}
                  className="border-bone-dark focus:border-gold rounded-sm mt-1"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label className="text-navy text-sm">Phone (optional)</Label>
                <Input
                  data-testid="join-phone"
                  type="tel"
                  value={joinForm.phone}
                  onChange={(e) => setJoinForm((f) => ({ ...f, phone: e.target.value }))}
                  className="border-bone-dark focus:border-gold rounded-sm mt-1"
                  placeholder="(xxx) xxx-xxxx"
                />
              </div>
              <div>
                <Label className="text-navy text-sm">Message (optional)</Label>
                <Textarea
                  data-testid="join-message"
                  value={joinForm.message}
                  onChange={(e) => setJoinForm((f) => ({ ...f, message: e.target.value }))}
                  rows={3}
                  className="border-bone-dark focus:border-gold rounded-sm mt-1"
                  placeholder="Tell us a little about yourself or your family..."
                />
              </div>
              <Button
                data-testid="join-submit-btn"
                className="w-full btn-gold uppercase tracking-wider font-medium"
                onClick={handleJoin}
                disabled={joinLoading}
              >
                {joinLoading
                  ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  : <Users className="h-4 w-4 mr-2" />
                }
                {joinLoading ? "Submitting..." : "Request to Join"}
              </Button>
              <p className="text-xs text-gray-400 text-center">
                Your Guide Pair will be in touch within a few days.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
